/**
 * Bootstrap providers for gozem-admin-web-v2 — the single `appConfig`
 * consumed by `main.ts` via `bootstrapApplication(...)`.
 *
 * Wires four cross-cutting concerns that every page depends on:
 *   1. Locale — `LOCALE_ID = "fr-FR"` plus `registerLocaleData(localeFr)`
 *      so pipes render without throwing.
 *   2. Router — with `PreloadAllModules` (every lazy chunk is fetched
 *      after first paint so navigation stays instant) and a custom
 *      chunk-mismatch `withNavigationErrorHandler` that force-reloads
 *      stale tabs after a deploy (see the comment inline).
 *   3. HTTP — `withFetch()` plus five interceptors in order: export paging
 *      (rewrites only the sort of an export-all page request) → auth →
 *      session-version → step-up → error. Order matters (auth must
 *      attach the Bearer BEFORE step-up can rewrite the challenge
 *      header, and error runs last so it sees the final response).
 *   4. `APP_INITIALIZER` × 2 — `initPermissions` (preload RBAC token
 *      set so first-render `*canPermission` directives resolve) and
 *      `initLanguage` (restore persisted UI language before first
 *      paint). Both swallow their errors so a bad-network boot still
 *      lands the admin on `/login`.
 *
 * `GZ_PERMISSION_CHECK` is the bridge from `@gozem/ui`'s declarative
 * `requires: string[]` on data-table actions to our
 * `PermissionsService.canAny(...)`. Any-match semantics.
 *
 * Related: `core/interceptors/*`, `core/services/permissions.service.ts`,
 * `core/services/language.service.ts`.
 */
import {APP_INITIALIZER, ApplicationConfig, LOCALE_ID, inject, provideZoneChangeDetection} from "@angular/core";
import {
    NoPreloading,
    PreloadAllModules,
    provideRouter,
    withComponentInputBinding,
    withNavigationErrorHandler,
    withPreloading
} from "@angular/router";
import {provideHttpClient, withFetch, withInterceptors} from "@angular/common/http";
import {registerLocaleData} from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import localeFrExtra from "@angular/common/locales/extra/fr";
import {firstValueFrom} from "rxjs";
import {GZ_DATA_TABLE_DEFAULT_VISIBLE, GZ_PERMISSION_CHECK} from "@gozem/ui";
import {provideTranslateService} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";

import {environment} from "../environments/environment";
import {routes} from "./app.routes";
import {authInterceptor} from "./core/interceptors/auth.interceptor";
import {silentScopeInterceptor} from "./core/http/silent-request";
import {errorInterceptor} from "./core/interceptors/error.interceptor";
import {exportPagingInterceptor} from "./core/interceptors/export-paging.interceptor";
import {sessionVersionInterceptor} from "./core/interceptors/session-version.interceptor";
import {stepUpInterceptor} from "./core/interceptors/step-up.interceptor";
import {AuthService} from "./core/services/auth.service";
import {DisplayTimeZoneService} from "./core/services/display-time-zone.service";
import {LanguageService} from "./core/services/language.service";
import {PermissionsService} from "./core/services/permissions.service";

// Register the French locale ONCE at bootstrap so DatePipe / DecimalPipe / etc.
// can render `| date: '...' : undefined : 'fr-FR'` without throwing.
registerLocaleData(localeFr, "fr-FR", localeFrExtra);

/**
 * Boot the PermissionsService BEFORE the router runs its first
 * navigation. Otherwise the first route's `permissionGuard` fires
 * against an empty token set — the guard has an internal fallback
 * that kicks the load, but doing it here means `*canPermission`
 * directives on the very first-render page also have a populated set.
 *
 * Skipped when the admin isn't authenticated yet (login page) — no
 * point calling /rbac/me/permissions with no session token.
 *
 * The `.catch(() => undefined)` swallows a bad-network / 500 on
 * `/rbac/me/permissions` so the app still boots — the service
 * gracefully falls into `legacy_fallback: true` mode (permissive
 * client-side; server is still authoritative).
 */
/**
 * Never let a boot-blocking call block the boot.
 *
 * Angular does not render `<app-root>` until every `APP_INITIALIZER` promise
 * settles. Both initializers below `.catch()` their errors — but a request that
 * is merely SLOW never rejects, so `.catch` does not cover it. One hung call to
 * `/rbac/me/permissions` or `/i18n/fr.json` left the admin on a blank page
 * indefinitely: no spinner, no error, no console output, nothing in the network
 * panel except a request still in flight. The e2e suite caught it as a page that
 * reported `bootstrapped=true` with `<app-root>` completely empty.
 *
 * Both services degrade correctly on their own — `PermissionsService` re-kicks
 * its load from the auth effect and falls into `legacy_fallback` on failure,
 * and every `| translate` pipe falls back to the key until the file lands — so
 * giving up on the WAIT costs nothing but a moment of unstyled content, while
 * not giving up costs the whole application.
 */
// 15s, not 5s.
//
// The budget exists to stop an UNBOUNDED wait, not to give up on a call that is
// merely slow. At 5s it fired routinely under an 8-worker e2e run — the SPA
// booted without its permission set, so every `*canPermission` control was
// missing until the call eventually landed, and five pages failed on a control
// that was going to appear. The same would happen to a real admin on a poor
// connection. 15s still bounds the blank page; it no longer punishes slowness.
const BOOT_BUDGET_MS = 15_000;

function withBootBudget<T>(what: string, work: Promise<T>): Promise<T | undefined> {
    let timer: ReturnType<typeof setTimeout>;
    const budget = new Promise<undefined>((resolve) => {
        timer = setTimeout(() => {
            // A warning, not an error: the app is about to boot correctly. It
            // is here so a blank-page report always has one line explaining
            // which call was still outstanding.

            console.warn(`[boot] ${what} exceeded ${BOOT_BUDGET_MS}ms — booting without it`);
            resolve(undefined);
        }, BOOT_BUDGET_MS);
    });
    return Promise.race([work, budget]).finally(() => clearTimeout(timer));
}

function initPermissions() {
    return () => {
        const auth = inject(AuthService);
        const perms = inject(PermissionsService);
        if (!auth.isAuthenticated()) {
            return Promise.resolve();
        }
        return withBootBudget(
            "permissions",
            firstValueFrom(perms.load()).catch(() => undefined)
        );
    };
}

/**
 * Restore the admin's persisted UI-language preference (`localStorage`
 * key `gz.admin.lang`, defaulting to `"fr"`) BEFORE the first component
 * renders. Without this, the app would briefly render with whatever
 * language `provideTranslateService` loads first, then flash to the
 * admin's actual preference once `LanguageService.bootstrap()` resolves
 * — this initializer makes that resolution happen during bootstrap
 * instead of after.
 *
 * `.catch(() => undefined)` mirrors `initPermissions`: a slow/broken
 * `/i18n/<lang>.json` fetch shouldn't block the app from booting. Every
 * `| translate` pipe falls back to the raw key (or French, via
 * `fallbackLang`) until the file loads.
 */
function initLanguage() {
    return () => {
        const language = inject(LanguageService);
        return withBootBudget(
            "language",
            firstValueFrom(language.bootstrap()).catch(() => undefined)
        );
    };
}

/**
 * Read the admin's display time zone (`timezone_for_display_date`) before the
 * first list is built, so its date filters use that zone's days from the first
 * request (core/services/display-time-zone.service.ts). Constructing the
 * service also arms its post-login reload. A failure or a slow answer leaves
 * the browser zone in force (the helper's fallback) and never blocks the boot.
 */
function initDisplayTimeZone() {
    return () => {
        const auth = inject(AuthService);
        const zone = inject(DisplayTimeZoneService);
        if (!auth.isAuthenticated()) {
            return Promise.resolve();
        }
        return withBootBudget(
            "display time zone",
            firstValueFrom(zone.load()).catch(() => undefined)
        );
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        {provide: LOCALE_ID, useValue: "fr-FR"},
        provideZoneChangeDetection({eventCoalescing: true}),
        // `withNavigationErrorHandler` catches the `Failed to fetch
        // dynamically imported module` rejection that fires whenever a
        // lazy-loaded route (`loadComponent` / `loadChildren`) tries to
        // pull a chunk name that no longer exists on the CDN.
        //
        // This is the classic Angular chunk-hash mismatch after a
        // deploy: the tab still holds the OLD `index.html`, so it
        // reaches for `chunk-WTZ6GFU2.js`, but the fresh build wrote
        // `chunk-XYZ.js`. The 404 propagates as a NavigationError.
        //
        // Rather than leave the user staring at a broken page, we
        // force a hard reload — that pulls the fresh `index.html`
        // pointing at the current chunk hashes, and the second
        // navigation lands cleanly. We match on message text so we
        // ONLY reload for the chunk-mismatch case; every other
        // navigation error still bubbles up normally to guards /
        // interceptors / Sentry.
        //
        // Longer-term fix lives at the CDN: `index.html` MUST be
        // `Cache-Control: no-cache`, hashed chunks
        // `public, max-age=31536000, immutable`. This handler is the
        // client-side belt to that server-side braces.
        provideRouter(
            routes,
            withComponentInputBinding(),
            // `PreloadAllModules` — after the initial view renders, the
            // router eagerly fetches EVERY lazy chunk in the background.
            // Two operational wins:
            //   1. Chunk-mismatch class of error: even when the tab
            //      still holds an old `index.html` after a deploy, all
            //      the chunks it references are already in the JS
            //      engine's module cache — the user clicks a route and
            //      it renders instantly instead of hitting the 404 →
            //      reload loop below. Applies to every admin whose
            //      session outlives a deploy (~all of them; admins
            //      keep tabs open for days).
            //   2. Perceived latency: the first click on any lazy
            //      route resolves off already-fetched bytes, no
            //      network wait. Especially noticeable on the
            //      /rbac/roles + /rbac/assignments + /admin-users
            //      triangle, which the same operator hits over and
            //      over during any RBAC ops session.
            //
            // Trade-off: the initial-boot network cost increases by
            // ~the sum of all lazy chunks. For an admin SPA with
            // ~200 routes and modest daily active count this is
            // fine — the bytes were going to be pulled anyway; we're
            // just amortising the fetch cost upfront rather than
            // paying it visibly on each click. `withNavigationErrorHandler`
            // below still guards the tail case where a preload race
            // loses to the deploy.
            // Production only.
            //
            // Preloading every one of the ~474 lazy chunks in the background is
            // the right trade for a long-lived admin session (see above). It is
            // the wrong trade for a short-lived one: each page load issues ~500
            // asset requests, and the browser serves them over the same six
            // connections the page's own data calls need. A form's country
            // lookup then queues behind hundreds of chunk downloads and its
            // select renders empty for tens of seconds — which is how this
            // surfaced, as e2e pages that "flaked" only at higher worker counts
            // and never one at a time.
            //
            // A dev build gets `NoPreloading`: chunks load on navigation, which
            // is what a developer and a test both want. Nothing about the
            // production build changes.
            withPreloading(environment.production ? PreloadAllModules : NoPreloading),
            withNavigationErrorHandler((error) => {
                const message = String((error as {message?: unknown})?.message ?? error);
                if (/dynamically imported module|Loading chunk .+ failed|Failed to fetch dynamically/i.test(message)) {
                    console.warn("[chunk-mismatch] reloading to pick up new build", message);
                    window.location.reload();
                }
            })
        ),
        // Interceptor order matters: `authInterceptor` adds the JWT
        // (must run BEFORE the request goes out); `stepUpInterceptor`,
        // `sessionVersionInterceptor` and `errorInterceptor` observe
        // the response and must run on the way back.
        //
        // Response-side interceptors run in REVERSE registration
        // order, so the response walks:
        //   errorInterceptor → sessionVersionInterceptor
        //     → stepUpInterceptor → authInterceptor's `finalize`
        //       → returns to caller
        //
        // `stepUpInterceptor` intentionally sits ABOVE
        // `authInterceptor` in the registration list — that means it
        // observes the response BEFORE `authInterceptor` finalises,
        // AND its retry (`next(req)`) walks through
        // `authInterceptor` again to pick up the FRESH token that
        // `MfaService.stepUp()` just stored on `AuthService`.
        //
        // A subtractive session-version bump still needs to trigger
        // the refresh before `errorInterceptor` swallows any 403,
        // so `sessionVersionInterceptor` stays between the two.
        provideHttpClient(
            withFetch(),
            withInterceptors([
                // FIRST: it reads a flag raised only during the synchronous
                // subscribe of an export page's list call (see its docblock),
                // before any interceptor that could defer the request.
                exportPagingInterceptor,
                // Also synchronous: marks the requests of a `runSilently()` call (a list's live refresh)
                // before authInterceptor counts them for the top-bar indicator.
                silentScopeInterceptor,
                authInterceptor,
                stepUpInterceptor,
                sessionVersionInterceptor,
                errorInterceptor
            ])
        ),
        {provide: APP_INITIALIZER, useFactory: initPermissions, multi: true},
        // ngx-translate — FR is the default/fallback language (matches
        // the `localeFr` registration above; nothing changes for an
        // admin who never opens the language picker). The loader reads
        // static JSON from the `public/` folder (copied verbatim to the
        // build output root by the `assets` glob in angular.json), so
        // files live at `/i18n/fr.json` + `/i18n/en.json`, NOT under
        // `/assets/i18n/` — this project's Angular 21 asset pipeline
        // has no `src/assets` folder; `public/` IS the assets root.
        provideTranslateService({
            fallbackLang: "fr",
            loader: provideTranslateHttpLoader({prefix: "/i18n/", suffix: ".json"})
        }),
        // Restore the persisted language BEFORE first render — see
        // `initLanguage` doc comment above.
        {provide: APP_INITIALIZER, useFactory: initLanguage, multi: true},
        {provide: APP_INITIALIZER, useFactory: initDisplayTimeZone, multi: true},
        // Bridge @gozem/ui's declarative permission gate to our
        // PermissionsService. Every `<gz-data-table>` row/batch action
        // annotated with `requires: string[]` is filtered against the
        // current admin's token set — hidden when the intersection
        // is empty. Any-match semantics (canAny). Reactive: the
        // resolver reads a signal, so the actions re-filter after
        // "Rafraîchir mes permissions" flips the token set.
        // A list shows its first 8 columns (or its curated `defaultVisible` set) until the admin picks columns from
        // the « Colonnes » menu — legacy showed every column, which on the big lists (orders 88, trips 163) buried
        // the useful ones. Support switch: localStorage `gz.admin.tableColumns` = "all" restores every column by
        // default in that browser, `hidden` ones included (the e2e harness sets it: its specs read columns a default
        // view hides).
        {
            provide: GZ_DATA_TABLE_DEFAULT_VISIBLE,
            useFactory: () => {
                try {
                    return globalThis.localStorage?.getItem("gz.admin.tableColumns") === "all" ? Infinity : 8;
                } catch {
                    return 8;
                }
            }
        },
        {
            provide: GZ_PERMISSION_CHECK,
            useFactory: () => {
                const perms = inject(PermissionsService);
                return (tokens: string[]) => perms.canAny(tokens);
            }
        }
    ]
};
